import { Boton } from '../../componentes/Boton';
import { Deslizador } from '../../componentes/Deslizador';
import { Icono } from '../../componentes/Icono';
import { ajustarRango, desdeEscrito, DESLIZADOR, DIAS, DIAS_CORTOS, diasCerrados, DURACION_MAXIMA, hastaEscrito, textoDias, textoTramo, TRAMOS_POR_BLOQUE, tramoNuevo, type Bloque, type Rango } from '../../logica/horario';
import { MARCAS } from './textos';

/**
 * La semana tipo por bloques (componentes.md §5g): cada bloque junta los días con los mismos tramos (píldoras Lu…Do; un
 * día de otro bloque sale en gris y, al tocarlo, pasa a este) y tiene de uno a tres tramos con RV Deslizador. Los tramos
 * no llevan nombre (G246): se leen por su hora. Debajo, «Añadir otro horario» y los días que cierra.
 */
interface Props {
    bloques: readonly Bloque[];
    /** Errores de cada tramo de cada bloque (locales y de la API). */
    errores: readonly (readonly string[])[];
    editable: boolean;
    /** El horario heredado de todo el negocio, atenuado. */
    atenuado?: boolean;
    porDefecto: Rango;
    movil: boolean;
    /** Sin semana todavía (aviso amarillo de 3.1 vacía): no se dice «Todos los días cerrados». */
    ocultarCerrados?: boolean;
    onCambiar: (bloques: Bloque[]) => void;
}

let siguiente = 0;
const claveNueva = () => {
    siguiente += 1;
    return `n${String(siguiente)}`;
};

/** «De 13:00 a 17:00», «De 20:00 a 02:00 (+1)» (G246: el tramo se lee por su hora, sin nombre). */
const tituloTramo = (r: Rango) => {
    const t = textoTramo(r);
    return `${t.charAt(0).toUpperCase()}${t.slice(1)}`;
};

export function Semana({ bloques, errores, editable, atenuado = false, porDefecto, movil, ocultarCerrados = false, onCambiar }: Props) {
    const copia = () => bloques.map((b) => ({ ...b, dias: [...b.dias], tramos: b.tramos.map((t) => ({ ...t })) }));
    const cambiar = (fn: (bl: Bloque[]) => void) => {
        const bl = copia();
        fn(bl);
        onCambiar(bl);
    };
    const usados = new Set(bloques.flatMap((b) => b.dias));
    const cerrados = diasCerrados(bloques);

    return (
        <div role="list" aria-label="Semana tipo" className={`rv-hor-semana${atenuado ? ' rv-hor-semana--atenuada' : ''}`}>
            {bloques.map((b, bi) => {
                const nombre = b.dias.length === 0 ? 'Horario sin días' : `Horario: ${textoDias(b.dias)}`;
                return (
                    <div key={b.clave} role="listitem" aria-label={nombre} className="rv-hor-bloque">
                        <div role="group" aria-label="Días de este horario" className="rv-hor-dias">
                            {DIAS_CORTOS.map((corto, i) => {
                                const dentro = b.dias.includes(i);
                                const otro = !dentro && usados.has(i);
                                const antes = dentro && b.dias.includes(i - 1);
                                const despues = dentro && b.dias.includes(i + 1);
                                return (
                                    <button
                                        key={corto}
                                        type="button"
                                        className={`rv-hor-dia${dentro ? ' rv-hor-dia--si' : ''}${otro ? ' rv-hor-dia--otro' : ''}${antes ? ' rv-hor-dia--antes' : ''}${despues ? ' rv-hor-dia--despues' : ''}`}
                                        aria-pressed={dentro}
                                        aria-label={`${DIAS[i] ?? ''}${otro ? ' (ahora en otro horario)' : ''}`}
                                        disabled={!editable}
                                        onClick={() => {
                                            cambiar((bl) => {
                                                const este = bl[bi];
                                                if (este === undefined) {
                                                    return;
                                                }
                                                if (este.dias.includes(i)) {
                                                    este.dias = este.dias.filter((x) => x !== i);
                                                } else {
                                                    for (const o of bl) {
                                                        o.dias = o.dias.filter((x) => x !== i);
                                                    }
                                                    este.dias = [...este.dias, i].sort((x, y) => x - y);
                                                }
                                            });
                                        }}
                                    >
                                        {corto}
                                    </button>
                                );
                            })}
                        </div>
                        {b.dias.length === 0 && editable && <p className="rv-hor-aviso">Elige los días de este horario.</p>}
                        {b.tramos.map((t, ti) => {
                            const etiqueta = `${nombre}${b.tramos.length > 1 ? `, tramo ${String(ti + 1)}` : ''}`;
                            const poner = (r: Rango) => {
                                cambiar((bl) => {
                                    const este = bl[bi];
                                    if (este !== undefined) {
                                        este.tramos[ti] = r;
                                    }
                                });
                            };
                            const escribir = (fn: typeof desdeEscrito) => (texto: string) => {
                                const r = fn(texto, t);
                                if (typeof r === 'string') {
                                    return r;
                                }
                                poner(r);
                                return '';
                            };
                            return (
                                <div key={`${b.clave}-${String(ti)}`} className="rv-hor-tramo">
                                    <div className="rv-hor-tramo__cab">
                                    <strong className="rv-hor-tramo__titulo">{tituloTramo(t)}</strong>
                                    {editable && (
                                        <div className="rv-hor-tramo__acciones">
                                            {b.tramos.length > 1 && (
                                                <Boton
                                                    tipo="secundario"
                                                    icono="x"
                                                    aria-label={`Quitar el tramo ${String(ti + 1)}`}
                                                    title="Quitar tramo"
                                                    onClick={() => {
                                                        cambiar((bl) => {
                                                            bl[bi]?.tramos.splice(ti, 1);
                                                        });
                                                    }}
                                                >
                                                    {movil ? '' : 'Quitar tramo'}
                                                </Boton>
                                            )}
                                            {ti === 0 && (
                                                <Boton
                                                    tipo="secundario"
                                                    rojo
                                                    icono="trash-2"
                                                    aria-label={`Eliminar el horario${b.dias.length === 0 ? ' sin días' : ` del ${textoDias(b.dias)}`}`}
                                                    title="Eliminar este horario"
                                                    onClick={() => {
                                                        cambiar((bl) => {
                                                            bl.splice(bi, 1);
                                                        });
                                                    }}
                                                >
                                                    {movil ? '' : 'Eliminar'}
                                                </Boton>
                                            )}
                                        </div>
                                    )}
                                    </div>
                                    <Deslizador
                                        etiqueta={etiqueta}
                                        desde={t.desde}
                                        hasta={t.hasta}
                                        min={DESLIZADOR.min}
                                        max={DESLIZADOR.max}
                                        paso={DESLIZADOR.paso}
                                        maxDist={DURACION_MAXIMA}
                                        marcas={MARCAS}
                                        desactivado={!editable}
                                        error={errores[bi]?.[ti] ?? ''}
                                        onRango={(desde, hasta) => {
                                            poner(ajustarRango({ desde, hasta }));
                                        }}
                                        {...(editable ? { escribirDesde: escribir(desdeEscrito), escribirHasta: escribir(hastaEscrito) } : {})}
                                    />
                                </div>
                            );
                        })}
                        {editable && b.tramos.length < TRAMOS_POR_BLOQUE && (
                            <div className="rv-hor-pie">
                                <Boton
                                    tipo="secundario"
                                    icono="plus"
                                    onClick={() => {
                                        cambiar((bl) => {
                                            const este = bl[bi];
                                            if (este !== undefined) {
                                                este.tramos.push(tramoNuevo(este.tramos, porDefecto));
                                            }
                                        });
                                    }}
                                >
                                    Añadir tramo
                                </Boton>
                                {b.tramos.length === 1 && <span>Para comida y cena, o si cierras a mediodía.</span>}
                            </div>
                        )}
                    </div>
                );
            })}
            <div className="rv-hor-pie">
                {editable && bloques.length < 7 && (
                    <Boton
                        tipo="secundario"
                        icono="plus"
                        onClick={() => {
                            cambiar((bl) => {
                                bl.push({ clave: claveNueva(), dias: [], tramos: [{ ...porDefecto }] });
                            });
                        }}
                    >
                        Añadir otro horario
                    </Boton>
                )}
                {cerrados.length > 0 && !ocultarCerrados && (
                    <span className="rv-hor-cerrado">
                        <Icono nombre="moon" />
                        <span>{cerrados.length === 7 ? 'Todos los días cerrados.' : `Cerrado: ${textoDias(cerrados)}.`}</span>
                    </span>
                )}
            </div>
        </div>
    );
}
